import React, { createContext } from 'react';
import { azureAdMsalConfig, ciamMsalConfig } from './MsalConfig';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { ErrorHandlingContext } from '../context/ErrorHandlingContext';
import { TokenRetrievalError } from '../utils/errors';
import { appConfig } from '../logic/appConfigProvider';
import { urls } from '../logic/urls';
import { urlCombine, getBaseUriWithoutParameters } from '../utils/misc';

 
const userIdTokenClaimsKey = 'userIdTokenClaimsKey';
const authMethodKey = 'authMethodKey';

export const authMethods = {
    AZURE_AD: 'AzureAD',
    CIAM: 'CIAM',
};

// Enum defines the possible states of the authenticationState property of the state of the AuthenticationProvider.
export const authenticationStates = {
    NOT_AUTHENTICATED: 'NotAuthenticated', // No user is currently logged-in
    AUTHENTICATING: 'Authenticating', // The redirect back from Microsoft has taken place, but the process of exchanging the authentication code for the tokens is still in progress.
    AUTHENTICATED: 'Authenticated', // The user is successfully logged in.
}


export const AuthenticationContext = createContext();

const loginScopes = [".default"];

export class AuthenticationProvider extends React.Component {
    static contextType = ErrorHandlingContext;

    constructor(props) {
        super(props);

        this.msalAuth = new PublicClientApplication(azureAdMsalConfig);
        this.ciamAuth = new PublicClientApplication(ciamMsalConfig);

        // Determine which auth method was used (persisted across redirects)
        this.activeAuthMethod = sessionStorage.getItem(authMethodKey) || null;

        const userAccount = this.getUserAccount();

        // After startup of the application it is not immediately known if there is a logged-in user or not, because the PublicClientApplication
        // might still be busy with requesting an access token based upon the received authorization code. Only when the handleRedirectPromise
        // has been invoked, the logged-in accounts can be queried reliably. 
        // There seems to be no way in the MSAL library to query if this work is still in progresss or not. This variable is a work-around 
        // to have this state available.
        this.isInProgress = true;

        this.state = {
            getAuthenticationState: () => { return this.getAuthenticationState() },
            isAuthenticated: () => { return this.getAuthenticationState() === authenticationStates.AUTHENTICATED},
            user: userAccount,
            userIdTokenClaims: this.retrieveUserIdTokenClaims(),
            originalUrl: null,
            authMethod: this.activeAuthMethod,
            login: (postRedirectUrl) => { 
                this.loginWithMethod(authMethods.AZURE_AD, postRedirectUrl);
            },
            loginWithCiam: (postRedirectUrl) => {
                this.loginWithMethod(authMethods.CIAM, postRedirectUrl);
            },
            logout: () => {
                const method = this.activeAuthMethod;
                this.clearSessionStorage();

                const redirectUrl = urlCombine(getBaseUriWithoutParameters(), urls.home);

                const msalInstance = method === authMethods.CIAM ? this.ciamAuth : this.msalAuth;

                const request = {
                    account: msalInstance.getActiveAccount(),
                    postLogoutRedirectUri: redirectUrl
                }

                msalInstance.logoutRedirect(request);
            },
            getApiTokenAsync: async () => {
                const isCiam = this.activeAuthMethod === authMethods.CIAM;
                const msalInstance = isCiam ? this.ciamAuth : this.msalAuth;
                const scope = isCiam ? appConfig.ciamMsalApiScope : appConfig.azureAdMsalApiScope;

                const accessTokenRequest = {
                    scopes: [scope],
                    account: this.state.user
                }

                try {
                    console.log(`[AuthenticationProvider, getApiTokenAsync] getApiTokenAsync invoked (method: ${this.activeAuthMethod})...`);

                    const accessToken = await msalInstance.acquireTokenSilent(accessTokenRequest);               

                    console.log("[AuthenticationProvider, getApiTokenAsync] Accesstoken for back-end API successfully retrieved!");

                    return accessToken;
                }
                catch (err) {
                    console.log(`[AuthenticationProvider, getApiTokenAsync] Error occurred while retrieving token for back-end API: ${err}`);

                    if (err instanceof InteractionRequiredAuthError) {
                        console.log("[AuthenticationProvider, getApiTokenAsync] Starting interactive attempt with redirect...");
                        msalInstance.acquireTokenRedirect(accessTokenRequest);                        
                        return;
                    } 
                    
                    throw new TokenRetrievalError(err, "Error occurred while retrieving token for back-end API");                   
                }
            },
            getGraphApiTokenAsync: async (executionCount = 0) => {
                // Graph API tokens are only available via Azure AD login
                const accessTokenRequest = {
                    scopes: ["https://graph.microsoft.com/User.Read"],
                    account: this.state.user
                }

                try {
                    console.log("[AuthenticationProvider, getGraphApiTokenAsync] getGraphApiTokenAsync invoked...");
        
                    const accessToken = await this.msalAuth.acquireTokenSilent(accessTokenRequest);
                    console.log("[AuthenticationProvider, getGraphApiTokenAsync] Accesstoken for the Graph API successfully retrieved!");
                    //console.log(accessToken.accessToken);

                    return accessToken;
                }
                catch (err) {
                    console.log(`[AuthenticationProvider, getGraphApiTokenAsync] Error occurred while retrieving token for Graph API: ${err}`);

                    if (err instanceof InteractionRequiredAuthError) {

                        // Graph token requests can fail for quit a while (45s seen during testing) after consent has been given.
                        //  Wait a maximum 2 minutes (24 x 5s) for a graph token request to succeed.
                        if (executionCount < 24) { 
                            console.log(`[AuthenticationProvider, getGraphApiTokenAsync] Error occurred while retrieving token for Graph API: ${err}. Retrying (${executionCount})...`);
                            await new Promise(r => setTimeout(r, 5000));
                            return this.state.getGraphApiTokenAsync(executionCount + 1);
                        }

                        console.log("[AuthenticationProvider, getGraphApiTokenAsync] Starting interactive attempt with redirect...");
                        this.msalAuth.acquireTokenRedirect(accessTokenRequest);
                        return;
                    }                  
                    
                    throw new TokenRetrievalError(err, "Error occurred while retrieving token for Graph API");    
                }
            },
        };
    }

    loginWithMethod = (method, postRedirectUrl) => {
        if (postRedirectUrl) {
            sessionStorage.setItem("postRedirectUrlKey", postRedirectUrl);
        } else {
            sessionStorage.removeItem("postRedirectUrlKey");
        }

        this.clearSessionStorage();

        sessionStorage.setItem(authMethodKey, method);
        this.activeAuthMethod = method;

        if (method === authMethods.CIAM) {
            const accessTokenRequest = {
                scopes: [appConfig.ciamMsalApiScope],
                prompt: "select_account"
            }
            this.ciamAuth.loginRedirect(accessTokenRequest);
        } else {
            const accessTokenRequest = {
                scopes: loginScopes,
                prompt: "select_account"
            }
            this.msalAuth.loginRedirect(accessTokenRequest);
        }
    }
    
    /// Method returns the user account object of the logged-in user or null if the user is not logged-in.
    getUserAccount = () => {
        if (this.activeAuthMethod === authMethods.CIAM) {
            return this.ciamAuth.getActiveAccount();
        }
        // Try Azure AD first, then CIAM
        return this.msalAuth.getActiveAccount() || this.ciamAuth.getActiveAccount();
    }

    /// Method returns the current authentication state which is either NOT_AUTHENTICATED, AUTHENTICATING or AUTHENTICATED.
    getAuthenticationState = () => {
        if (this.getUserAccount()) {
            return authenticationStates.AUTHENTICATED; 
        }

        if (this.isInProgress) {
            return authenticationStates.AUTHENTICATING;
        }   
        
        return authenticationStates.NOT_AUTHENTICATED;
    }

    // Method stores the provided user Id token claims in the session storage.
    // These user Id token claims are received in the redirect callback of the MSAL library and are not part of the account information and cannot be retrieved from the 
    // MSAL library. For this reason these claims are persisted in the session storage so they can be used when the page is refreshed to set the initial state of the 
    // authenticationContext correctly.
    persistUserIdTokenClaims = (userIdTokenClaims) => {
        const serialized = JSON.stringify(userIdTokenClaims);
        sessionStorage.setItem(userIdTokenClaimsKey, serialized);
    }

    // Method retrieves the persisted user Id token claims from the session storage.
    // In case no user is logged-in, null is returned.
    retrieveUserIdTokenClaims = () => {
        const serialized = sessionStorage.getItem(userIdTokenClaimsKey);

        if (!serialized) {
            return null;
        }

        const userIdTokenClaims = JSON.parse(serialized);
        return userIdTokenClaims;
    }

    // Method clears all information that is persisted in the session storage by this module.
    clearSessionStorage = () => {    
        sessionStorage.removeItem(userIdTokenClaimsKey);
        // Note: authMethodKey is NOT cleared here so we know which method to use after redirect
    }

    handleMsalRedirect = async (msalInstance, methodName) => {
        try {
            const tokenResponse = await msalInstance.handleRedirectPromise();
            if (tokenResponse !== null) {
                let account = tokenResponse.account;
                console.log(`[handleRedirectPromise, ${methodName}] User '${account.username}' successfully logged in.`);
                
                msalInstance.setActiveAccount(account);
                this.activeAuthMethod = methodName === 'CIAM' ? authMethods.CIAM : authMethods.AZURE_AD;
                sessionStorage.setItem(authMethodKey, this.activeAuthMethod);
        
                this.persistUserIdTokenClaims(tokenResponse.idTokenClaims);            
                this.setState({
                    user: tokenResponse.account,
                    userIdTokenClaims: tokenResponse.idTokenClaims,
                    authMethod: this.activeAuthMethod,
                });
        
                return true; // handled
            }
        } catch (error) {
            console.log(`[handleRedirectPromise, ${methodName}] ${error.message}`);
            msalInstance.setActiveAccount(null);
        }
        return false; // not handled
    }

    async componentDidMount() {
        console.log("[AuthenticationProvider, componentDidMount] Method invoked...");

        await this.msalAuth.initialize();
        await this.ciamAuth.initialize();

        // Register for the callbacks from both MSAL instances.
        // The success callback ('then' part) is invoked immediately after registration when the user is either not authorized or already authorized. 
        // In that case no token is provided as parameter.
        // When the application is starting up because of a redirect back from Azure, the MSAL library will start to resolve the OAuth authorization code for
        // an id_token and an access_token. When this process is finished the 'then' part is invoked and the tokenResponse parameter will contain 
        // all details from the response from Azure AD or External ID.
        // Try to handle redirect from whichever provider was used
        const storedMethod = sessionStorage.getItem(authMethodKey);
        
        let handled = false;
        if (storedMethod === authMethods.CIAM) {
            handled = await this.handleMsalRedirect(this.ciamAuth, 'CIAM');
            if (!handled) {
                handled = await this.handleMsalRedirect(this.msalAuth, 'AzureAD');
            }
        } else {
            handled = await this.handleMsalRedirect(this.msalAuth, 'AzureAD');
            if (!handled) {
                handled = await this.handleMsalRedirect(this.ciamAuth, 'CIAM');
            }
        }

        this.isInProgress = false;

        if (!handled) {
            console.log("[handleRedirectPromise] No change in logged-in state of user.");
        }
    }

    render() {
        return (
            <AuthenticationContext.Provider value={this.state}>
                {this.props.children}
            </AuthenticationContext.Provider>
        )
    }  
}